import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  List,
  Space,
  theme,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect, useMemo, useState } from "react";
import {
  addWellbeingMaterial,
  updateWellbeingMaterial,
  deleteWellbeingMaterial,
  getWellbeingMaterialCategories,
  uploadMaterialFile,
  getMaterialFiles,
  previewMaterialFile,
  deleteMaterialFile,
  getMaterialKpis,
  addMaterialKpis,
  deleteMaterialKpi,
  getKpis,
  getKpiMatrixRanges,
} from "../../repository/data.repository";
import DynamicForm from "../../controls/DynamicForm";
import ComboBox from "../../controls/ComboBox";
import BandTag from "../../controls/BandTag";
import {
  DynamicControl,
  DynamicSchema,
} from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import {
  WellbeingMaterial,
  WellbeingMaterialFile,
  WellbeingMaterialKpi,
  WellbeingMaterialType,
} from "../../repository/data.default";
import { WellbeingMaterialForm } from "./WellbeingMaterialFormDialog.default";
import { typeRequest } from "./WellbeingMaterialsView.default";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { InputObject } from "../../controls/default";
import { retry } from "../../utils/http";
import { getProblemMessage } from "../../utils/http";

export interface WellbeingMaterialFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: WellbeingMaterial;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function WellbeingMaterialFormDialog(props: WellbeingMaterialFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<WellbeingMaterialForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  // The material we are editing. In "add" mode this is undefined until the core record is created
  // (two-step create: save core fields first to obtain a uid, then attach files/KPI tags below).
  const [material, setMaterial] = useState<WellbeingMaterial | undefined>(
    props.initialValues,
  );

  // Files (Document materials)
  const [files, setFiles] = useState<WellbeingMaterialFile[]>([]);
  // True while a file upload POST is in flight (disables the Upload button to prevent double-posts).
  const [uploading, setUploading] = useState(false);
  // KPI tags
  const [kpiTags, setKpiTags] = useState<WellbeingMaterialKpi[]>([]);
  // KPI builder row state (its own little form)
  const [kpiForm] = Form.useForm();
  const kpiValues = Form.useWatch<{
    readonly kpi?: InputObject;
    readonly band?: InputObject;
  }>([], kpiForm);

  const isEditing = !!material?.uid;

  // The Type selected right now (drives the conditional content control).
  const selectedType: WellbeingMaterialType | undefined =
    (values?.type?.value as WellbeingMaterialType) ?? material?.type;

  // Lifecycle

  useEffect(() => {
    if (props.open) {
      setMaterial(props.initialValues);
      if (props.initialValues?.uid) {
        // Seed the form from the existing material (comboboxes need {display,value}).
        form.setFieldsValue({
          name: props.initialValues.name,
          shortDescription: props.initialValues.shortDescription,
          description: props.initialValues.description,
          type: props.initialValues.type
            ? {
                display: props.initialValues.type,
                value: props.initialValues.type,
              }
            : undefined,
          externalUrl: props.initialValues.externalUrl,
          bodyHtml: props.initialValues.bodyHtml,
          category: props.initialValues.categoryUid
            ? {
                display: props.initialValues.categoryName,
                value: props.initialValues.categoryUid,
              }
            : undefined,
          isPublic: props.initialValues.isPublic,
          isPublished: props.initialValues.isPublished,
        });
        loadFiles(props.initialValues.uid);
        loadKpis(props.initialValues.uid);
      }
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      // Require the actually-required core fields (Name + Type) before enabling Create/Save.
      .then(() => setValid(!!values?.name && !!values?.type?.value))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  // Loaders

  function loadKpis(materialUid: string): void {
    retry(() => getMaterialKpis(materialUid))
      .then(({ data }) => setKpiTags(data))
      .catch(() => setKpiTags([]));
  }

  function loadFiles(materialUid: string): void {
    getMaterialFiles(materialUid)
      .then((data) => setFiles(data))
      .catch(() => setFiles([]));
  }

  // Core save (step 1). On add, creates the record then transitions to editing so files/tags attach.
  function onSaveCore(): void {
    const v = values;
    setLoading(true);
    setErrorMessage(undefined);

    const type = v.type?.value as WellbeingMaterialType;
    const payload = {
      name: v.name,
      shortDescription: v.shortDescription,
      description: v.description,
      type,
      // Only persist the content field relevant to the chosen Type so a stale value left over from a
      // previous Type selection isn't saved (Article → bodyHtml; Video/Link → externalUrl; Document → files).
      externalUrl: type === "Video" || type === "Link" ? v.externalUrl : undefined,
      bodyHtml: type === "Article" ? v.bodyHtml : undefined,
      categoryUid: v.category?.value,
      isPublic: !!v.isPublic,
      isPublished: !!v.isPublished,
    };

    if (!isEditing) {
      addWellbeingMaterial(payload)
        .then((created: WellbeingMaterial) => {
          setLoading(false);
          // Two-step: keep the dialog open in editing state so files/KPI tags can be attached.
          setMaterial(created);
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else {
      updateWellbeingMaterial({
        uid: material!.uid,
        rowVersion: material!.rowVersion,
        ...payload,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onDelete(): void {
    setLoading(true);
    setErrorMessage(undefined);
    deleteWellbeingMaterial(props.initialValues?.uid!)
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
      });
  }

  // Files

  function onUpload(file: File): boolean {
    const uid = material?.uid;
    if (!uid || uploading) {
      return false;
    }
    setUploading(true);
    uploadMaterialFile(uid, file)
      .then(() => {
        loadFiles(uid); // re-read so the new file shows with its real uid (upload returns 204)
        message.success("File uploaded");
      })
      .catch((err) => message.error(getProblemMessage(err)))
      .finally(() => setUploading(false));
    return false; // prevent antd's default upload
  }

  function onPreviewFile(fileUid: string): void {
    previewMaterialFile(fileUid).catch((err) =>
      message.error(getProblemMessage(err)),
    );
  }

  function onDeleteFile(fileUid: string): void {
    deleteMaterialFile(fileUid)
      .then(() => setFiles((prev) => prev.filter((f) => f.uid !== fileUid)))
      .catch((err) => message.error(getProblemMessage(err)));
  }

  // KPI tags

  const kpiRequest = createSearchRequest(getKpis, "name");

  // Bands cascade on the chosen KPI (its matrix ranges). Scoped via the shared cascade helper +
  // dependsOn:["kpi"] on the band control (below) so changing the KPI auto-resets a stale band.
  // Band optional = "Any result".
  const bandRequest = createScopedFilterRequest(getKpiMatrixRanges, "label", () => [
    ["kpiDefinitionUid", kpiForm.getFieldValue("kpi")?.value],
  ]);

  function onAddKpiTag(): void {
    const kpi = kpiValues?.kpi;
    if (!kpi?.value || !material?.uid) {
      return;
    }
    const band = kpiValues?.band;
    addMaterialKpis(material.uid, [
      { kpiDefinitionUid: kpi.value, resultingRangeUid: band?.value },
    ])
      .then(() => {
        kpiForm.resetFields();
        loadKpis(material!.uid);
      })
      .catch((err) => message.error(getProblemMessage(err)));
  }

  function onRemoveKpiTag(tagUid: string): void {
    deleteMaterialKpi(tagUid)
      .then(() => setKpiTags((prev) => prev.filter((t) => t.uid !== tagUid)))
      .catch((err) => message.error(getProblemMessage(err)));
  }

  // Methods

  function onCancel(): void {
    // If we created the record mid-flow, surface it to the list before closing.
    if (props.mode === "add" && isEditing) {
      props.onFinish?.();
    } else {
      props.onCancel?.();
    }
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    setMaterial(undefined);
    setFiles([]);
    setUploading(false);
    setKpiTags([]);
    form.resetFields();
    kpiForm.resetFields();
  }

  // The content control rebuilds when the Type changes (conditional-content). Memoized off
  // selectedType so the DynamicForm receives a fresh schema only when the gate flips.
  const schema: DynamicSchema = useMemo(() => {
    const contentControls: DynamicControl[] = [];
    if (selectedType === "Article") {
      contentControls.push({
        type: "editorbox",
        name: "bodyHtml",
        label: "Body",
        rows: 8,
        richToolbar: true,
        tooltip:
          "Rich-text article body. Use the toolbar to format text, add links, and insert images by URL (host the image on a public link).",
      });
    } else if (selectedType === "Video" || selectedType === "Link") {
      contentControls.push({
        type: "textbox",
        name: "externalUrl",
        label: "External URL",
        rules: [{ required: true, message: "External URL is required" }],
        tooltip:
          selectedType === "Video"
            ? "Link to the video (e.g. a YouTube URL)."
            : "Link to the external resource.",
      });
    }
    // Document content (file upload + list) is rendered outside DynamicForm below.

    return {
      groups: [
        {
          controls: [
            {
              type: "textbox",
              name: "name",
              label: "Name",
              rules: [{ required: true, message: "Name is required" }],
            },
            {
              type: "textbox",
              name: "shortDescription",
              label: "Short Description",
              tooltip: "A one-line summary shown in material listings.",
            },
            {
              type: "areabox",
              name: "description",
              label: "Description",
              tooltip: "Full descriptive text shown on the material.",
            },
            {
              type: "combobox",
              name: "type",
              label: "Type",
              request: typeRequest,
              rules: [{ required: true, message: "Type is required" }],
              tooltip:
                "Article uses a rich-text body · Video and Link use an external URL · Document lets you attach files.",
            },
            ...contentControls,
            {
              type: "combobox",
              name: "category",
              label: "Category",
              request: createSearchRequest(
                getWellbeingMaterialCategories,
                "name",
              ),
              tooltip:
                "Groups the material in the Library (e.g. by wellbeing domain).",
            },
            {
              type: "togglebox",
              name: "isPublic",
              label: "Is Public",
              checkedText: "Public",
              unCheckedText: "Private",
              tooltip:
                "Public materials are reachable by everyone on the platform — no assignment needed.",
            },
            {
              type: "togglebox",
              name: "isPublished",
              label: "Published",
              checkedText: "Published",
              unCheckedText: "Draft",
              tooltip:
                "Drafts stay hidden from everyone. Publish to make the material reachable via public, assignment, or KPI targeting.",
            },
          ],
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const title = modalTitle(
    props.mode === "add" && isEditing ? "update" : props.mode,
    "Material",
    material?.name ?? props.initialValues?.name,
  );

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "large"}
      title={title}
      onCancel={onCancel}
      actions={
        props.mode === "delete" ? (
          <ModalFooter
            mode="delete"
            loading={loading}
            onCancel={onCancel}
            onSubmit={onDelete}
          />
        ) : (
          <Space className="flex flex-row items-center justify-end">
            <Button disabled={loading} onClick={onCancel}>
              {props.mode === "add" && isEditing ? "Done" : "Cancel"}
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!valid}
              onClick={onSaveCore}
            >
              {!isEditing ? "Create" : "Save"}
            </Button>
          </Space>
        )
      }
    >
      {props.mode === "delete" ? (
        <ModalDeleteConfirmation
          entityLabel="material"
          entityName={props.initialValues?.name}
        />
      ) : (
        <Col className="flex max-h-[560px] w-full flex-col overflow-y-auto">
          {props.mode === "add" && !isEditing && (
            <Alert
              className="mb-3 shrink-0"
              type="info"
              showIcon
              message="Save the material first to attach files and KPI tags."
            />
          )}
          <DynamicForm className="w-full shrink-0" form={form} schema={schema} />

          <Divider className="my-2 shrink-0" />

          {/* Document content: file upload + attached files list (only once the material exists). */}
          {selectedType === "Document" && (
            <Col className="mb-2 flex shrink-0 flex-col">
              <Typography.Text
                className="mb-1 text-sm"
                style={{ color: token.colorTextDescription }}
              >
                Attached Files
              </Typography.Text>
              {!isEditing ? (
                <Typography.Text type="secondary">
                  Create the material first to attach files.
                </Typography.Text>
              ) : (
                <>
                  <Typography.Text type="secondary" className="mb-2 text-xs">
                    Upload PDFs, Office documents, or images for this Document
                    material.
                  </Typography.Text>
                  <Upload
                    multiple={false}
                    showUploadList={false}
                    beforeUpload={(file) => onUpload(file as File)}
                    fileList={[] as UploadFile[]}
                  >
                    <Button>Upload File</Button>
                  </Upload>
                  {!!files.length && (
                    <List
                      className="mt-2"
                      style={{
                        backgroundColor: token.colorBgLayout,
                        borderRadius: token.borderRadius,
                      }}
                      dataSource={files}
                      renderItem={(file) => (
                        <List.Item
                          className="p-1 pl-2 pr-2"
                          actions={[
                            <Button
                              type="link"
                              size="small"
                              onClick={() => onPreviewFile(file.uid)}
                            >
                              Preview
                            </Button>,
                            <Button
                              type="link"
                              size="small"
                              danger
                              onClick={() => onDeleteFile(file.uid)}
                            >
                              Delete
                            </Button>,
                          ]}
                        >
                          <Typography.Text>
                            {file.fileName}
                            {file.fileExtension}
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  )}
                </>
              )}
            </Col>
          )}

          {/* KPI tags builder (only once the material exists). */}
          <Col className="mt-2 flex shrink-0 flex-col">
            <Typography.Text
              className="mb-1 text-sm"
              style={{ color: token.colorTextDescription }}
            >
              KPI Tags
            </Typography.Text>
            {!isEditing ? (
              <Typography.Text type="secondary">
                Create the material first to add KPI tags.
              </Typography.Text>
            ) : (
              <>
                <Typography.Text type="secondary" className="mb-2 text-xs">
                  Tag the material to a KPI so it's automatically recommended to
                  users whose latest evaluation lands in the chosen band.
                </Typography.Text>
                <Form
                  form={kpiForm}
                  layout="vertical"
                  className="my-form-item flex flex-row items-end gap-2"
                  // Cascade reset: changing the KPI clears any stale band so it can't be submitted
                  // against a different KPI (mirrors DynamicForm's dependsOn:["kpi"] behaviour).
                  onValuesChange={(changed) => {
                    if ("kpi" in changed) {
                      kpiForm.setFieldValue("band", undefined);
                    }
                  }}
                >
                  <Form.Item
                    name="kpi"
                    label="KPI"
                    className="m-0 flex-auto"
                    tooltip="The KPI metric whose results should surface this material."
                  >
                    <ComboBox className="w-full" request={kpiRequest} />
                  </Form.Item>
                  <Form.Item
                    name="band"
                    label="Band (optional)"
                    className="m-0 flex-auto"
                    tooltip="Leave empty to target any result for this KPI; pick a band to target only users whose latest result is in that band."
                  >
                    <ComboBox
                      className="w-full"
                      request={bandRequest}
                      placeholder={
                        kpiValues?.kpi?.value
                          ? "Any result"
                          : "Select a KPI first"
                      }
                      disabled={!kpiValues?.kpi?.value}
                    />
                  </Form.Item>
                  <Button onClick={onAddKpiTag} disabled={!kpiValues?.kpi}>
                    Add
                  </Button>
                </Form>
                {!!kpiTags.length && (
                  <List
                    className="mt-2"
                    style={{
                      backgroundColor: token.colorBgLayout,
                      borderRadius: token.borderRadius,
                    }}
                    dataSource={kpiTags}
                    renderItem={(tag) => (
                      <List.Item
                        className="p-1 pl-2 pr-2"
                        actions={[
                          <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => onRemoveKpiTag(tag.uid)}
                          >
                            Remove
                          </Button>,
                        ]}
                      >
                        <Space>
                          <Typography.Text>
                            {tag.kpiDefinitionName}
                          </Typography.Text>
                          {/* Band-scoped tag, or an "Any result" tag when band-agnostic. */}
                          <BandTag
                            label={tag.resultingRangeLabel ?? "Any result"}
                            color={tag.resultingRangeLabel ? "blue" : undefined}
                          />
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
              </>
            )}
          </Col>
        </Col>
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default WellbeingMaterialFormDialog;
